import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  attachSessionShading,
  darkTheme,
  isKnownInterval,
  registerInterval,
  SessionCalendar,
  type ChartTheme,
  type SeriesApi,
} from 'openalgo-charts';
import {
  createLayoutsController,
  createWidget,
  mountIndicatorSettings,
  openLayoutsMenu,
  widgetLayoutTarget,
  type LayoutsController,
  type Widget,
} from 'openalgo-charts/widget';
import 'openalgo-charts/indicators';
import type { CandleBar } from './barSource';
import {
  applyCrosshair,
  armSymbolField,
  bindChartWatermark,
  createDrawingStore,
  paintCrosshairButton,
  paintLayoutsButton,
  paintSessionControl,
  watchSessionSettings,
} from './chartDesk';
import {
  fromOpenAlgoInterval,
  OPENALGO_INTERVALS,
  tailUpdate,
  toOpenAlgoBars,
  type OpenAlgoBar,
} from './openAlgoBars';
import {
  dropViewport,
  lookupZoom,
  placeZoom,
  shadeCss,
  sharedZoomKey,
  type SessionShade,
  type ZoomWindow,
} from './savedChart';
import { ZERODAY_ID } from './zerodayIndicator';
import {
  chartWidgetWorkspaceOptions,
  keepSymbolLayoutTarget,
  readLayoutHost,
  type LayoutHostLook,
} from './chartWorkspace';

const UP = '#1C76A2';
const DOWN = '#D8483B';

function instanceIdFrom(payload: unknown): string {
  if (typeof payload !== 'object' || payload === null || !('instanceId' in payload)) return '';
  const id = payload.instanceId;
  return typeof id === 'string' ? id : '';
}

/** The legend gear emits `indicatorSettings` and does not open a dialog. */
function openIndicatorSettings(widget: Widget, instanceId: string): void {
  if (!instanceId) return;
  try {
    mountIndicatorSettings(widget.context, undefined, { instanceId });
  } catch {
    // The dialog host is missing.
  }
}

export interface OpenAlgoCandleChartProps {
  bars: readonly CandleBar[];
  seriesKey: string;
  symbol: string;
  intervalCode: string;
  chartType: string;
  extendedHours: boolean;
  sessionEnabled: boolean;
  crosshair: boolean;
  seedIndicator: boolean;
  savedState: unknown;
  drawings: Record<string, unknown>;
  zoom: Record<string, ZoomWindow>;
  sessionShade: SessionShade;
  onSymbol?: (symbol: string) => void;
  onInterval?: (code: string) => void;
  onChartType?: (id: string) => void;
  onChrome?: (slot: HTMLElement | null) => void;
  onExtendedHours?: (extended: boolean) => void;
  onCrosshair?: (on: boolean) => void;
  onState?: (state: unknown) => void;
  onDrawings?: (docs: Record<string, unknown>) => void;
  onView?: (code: string, zoom: ZoomWindow) => void;
  onSessionShade?: (shade: SessionShade) => void;
}

let monthIntervalReady = false;

/** `1mo` is not a built-in token. The widget refuses an interval it does not know. */
function ensureMonthInterval(): void {
  if (monthIntervalReady) return;
  monthIntervalReady = true;
  if (isKnownInterval('1mo')) return;
  registerInterval({
    code: '1mo',
    bucketing: { mode: 'calendar', unit: 'month', count: 1 },
  });
}

/** US cash hours. Pre is 04:00–09:30. Post is 16:00–20:00. */
function usEquityCalendar(): SessionCalendar {
  return new SessionCalendar({
    timezone: 'America/New_York',
    sessions: ['0930-1600:23456'],
    preMarketMinutes: 330,
    postMarketMinutes: 240,
  });
}

function candleTheme(): ChartTheme {
  return {
    ...darkTheme,
    background: '#0A0A0A',
    upColor: UP,
    downColor: DOWN,
    wickUpColor: UP,
    wickDownColor: DOWN,
    lineColor: UP,
    lastPriceUp: UP,
    lastPriceDown: DOWN,
    lastPriceText: '#0A0A0A',
  };
}

function linkSlot(root: HTMLElement): HTMLElement {
  const bar = root.querySelector('.oac-topbar');
  if (!(bar instanceof HTMLElement)) return root;
  const existing = bar.querySelector('.qf-chart-link');
  if (existing instanceof HTMLElement) return existing;
  const slot = document.createElement('span');
  slot.className = 'qf-chart-link';
  slot.style.display = 'inline-flex';
  slot.style.alignItems = 'center';
  slot.style.flex = '0 0 auto';
  // The link icon belongs to the ticker, so it sits right after the symbol box.
  const symbol = bar.querySelector('.oac-sym');
  if (symbol) symbol.after(slot);
  else bar.prepend(slot);
  return slot;
}

function pushSeries(
  series: SeriesApi,
  mapped: readonly OpenAlgoBar[],
  seriesKey: string,
  keyRef: { current: string | null },
  prevRef: { current: OpenAlgoBar[] },
): 'tail' | 'replace' {
  const sameSeries = keyRef.current === seriesKey && prevRef.current.length > 0;
  const tail = sameSeries ? tailUpdate(prevRef.current, mapped) : null;
  if (tail) {
    series.update(tail);
    prevRef.current = mapped.slice();
    keyRef.current = seriesKey;
    return 'tail';
  }
  series.setData(mapped);
  prevRef.current = mapped.slice();
  keyRef.current = seriesKey;
  return 'replace';
}

function keptRange(chart: Widget['chart'], sameSeries: boolean): ZoomWindow | null {
  if (!sameSeries) return null;
  try {
    const range = chart.getVisibleLogicalRange();
    if (range.to <= range.from) return null;
    return { from: range.from, to: range.to };
  } catch {
    return null;
  }
}

/**
 * OpenAlgo candle chart. The widget is created once so a symbol change does
 * not wipe the drawings. Bars are pushed onto `widget.series`.
 */
export function OpenAlgoCandleChart({
  bars,
  seriesKey,
  symbol,
  intervalCode,
  chartType,
  extendedHours,
  sessionEnabled,
  crosshair,
  seedIndicator,
  savedState,
  drawings,
  zoom,
  sessionShade,
  onSymbol,
  onInterval,
  onChartType,
  onChrome,
  onExtendedHours,
  onCrosshair,
  onState,
  onDrawings,
  onView,
  onSessionShade,
}: OpenAlgoCandleChartProps): ReactElement {
  const hostRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<Widget | null>(null);
  const keyRef = useRef<string | null>(null);
  const prevRef = useRef<OpenAlgoBar[]>([]);
  const symbolRef = useRef(symbol);
  const intervalRef = useRef(intervalCode);
  const chartTypeRef = useRef(chartType);
  const onSymbolRef = useRef(onSymbol);
  const onIntervalRef = useRef(onInterval);
  const onChartTypeRef = useRef(onChartType);
  const onChromeRef = useRef(onChrome);
  const onExtendedRef = useRef(onExtendedHours);
  const onCrosshairRef = useRef(onCrosshair);
  const onStateRef = useRef(onState);
  const onDrawingsRef = useRef(onDrawings);
  const onViewRef = useRef(onView);
  const onShadeRef = useRef(onSessionShade);
  const extendedOnRef = useRef(extendedHours);
  const sessionEnabledRef = useRef(sessionEnabled);
  const crosshairOnRef = useRef(crosshair);
  const shadeRef = useRef(sessionShade);
  const savedRef = useRef(savedState);
  const drawingsRef = useRef(drawings);
  const seedRef = useRef(seedIndicator);
  const viewsRef = useRef<Record<string, ZoomWindow>>({ ...zoom });
  const acceptEditsRef = useRef(false);
  const restoringRef = useRef(false);
  const openLayoutsRef = useRef<((anchor: HTMLElement) => void) | null>(null);
  const blankedCrosshair = useRef<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    symbolRef.current = symbol;
    intervalRef.current = intervalCode;
    chartTypeRef.current = chartType;
    onSymbolRef.current = onSymbol;
    onIntervalRef.current = onInterval;
    onChartTypeRef.current = onChartType;
    onChromeRef.current = onChrome;
    onExtendedRef.current = onExtendedHours;
    onCrosshairRef.current = onCrosshair;
    onStateRef.current = onState;
    onDrawingsRef.current = onDrawings;
    onViewRef.current = onView;
    onShadeRef.current = onSessionShade;
    extendedOnRef.current = extendedHours;
    sessionEnabledRef.current = sessionEnabled;
    crosshairOnRef.current = crosshair;
    shadeRef.current = sessionShade;
  }, [
    symbol,
    intervalCode,
    chartType,
    onSymbol,
    onInterval,
    onChartType,
    onChrome,
    onExtendedHours,
    onCrosshair,
    onState,
    onDrawings,
    onView,
    onSessionShade,
    extendedHours,
    sessionEnabled,
    crosshair,
    sessionShade,
  ]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    let widget: Widget | null = null;
    const stops: Array<() => void> = [];
    const workspace = chartWidgetWorkspaceOptions();
    try {
      ensureMonthInterval();
      widget = createWidget(el, {
        symbol: symbolRef.current,
        interval: intervalRef.current,
        intervals: OPENALGO_INTERVALS,
        chartType: chartTypeRef.current,
        timezone: 'America/New_York',
        theme: candleTheme(),
        sessionCalendar: usEquityCalendar(),
        zoomAnchor: 'right',
        branding: false,
        sessionShading: false,
        topbar: true,
        rail: true,
        indicators: true,
        statusline: true,
        bottombar: true,
        typingNavigation: true,
        drawingStore: createDrawingStore(drawingsRef.current, (docs) => {
          if (!acceptEditsRef.current || restoringRef.current) return;
          onDrawingsRef.current?.(docs);
        }),
        // Groups live in the indicator picker. Layouts are our button, so
        // opening the page does not apply one layout to every chart.
        ...workspace,
      });
      widget.root.style.height = '100%';
      widget.root.style.minHeight = '0';
      widgetRef.current = widget;
      keyRef.current = null;
      prevRef.current = [];
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Chart failed to start';
      queueMicrotask(() => setFailed(message));
      return undefined;
    }
    if (!widget) return undefined;

    let reportTimer: ReturnType<typeof setTimeout> | null = null;
    const report = (): void => {
      if (!acceptEditsRef.current || restoringRef.current) return;
      try {
        widget?.instrumentDrawings?.save();
        onStateRef.current?.(dropViewport(widget?.getState()));
      } catch {
        // A destroyed widget throws. The pane is already going away.
      }
    };
    const reportSoon = (): void => {
      if (reportTimer != null) clearTimeout(reportTimer);
      reportTimer = setTimeout(() => {
        reportTimer = null;
        report();
      }, 200);
    };
    const rememberView = (): void => {
      if (!acceptEditsRef.current || restoringRef.current || !widget) return;
      try {
        const range = widget.chart.getVisibleLogicalRange();
        if (range.to <= range.from) return;
        const count = prevRef.current.length;
        const saved: ZoomWindow = count > 0
          ? { from: range.from, to: range.to, bars: count }
          : { from: range.from, to: range.to };
        const code = intervalRef.current;
        viewsRef.current[sharedZoomKey(code)] = saved;
        onViewRef.current?.(code, saved);
      } catch {
        // The scale is not ready. The next pan records the window.
      }
      reportSoon();
    };
    const toggleCrosshair = (): void => {
      onCrosshairRef.current?.(!crosshairOnRef.current);
    };
    const chooseSession = (extended: boolean): void => {
      onExtendedRef.current?.(extended);
    };
    const readLook = (): LayoutHostLook => ({
      extendedHours: extendedOnRef.current && sessionEnabledRef.current,
      crosshair: crosshairOnRef.current,
      sessionShade: shadeRef.current,
    });
    const applyLook = (saved: ReturnType<typeof readLayoutHost>): void => {
      if (typeof saved.extendedHours === 'boolean') onExtendedRef.current?.(saved.extendedHours);
      if (typeof saved.crosshair === 'boolean') onCrosshairRef.current?.(saved.crosshair);
      if (saved.sessionShade) onShadeRef.current?.(saved.sessionShade);
    };
    let layouts: LayoutsController | null = null;
    try {
      layouts = createLayoutsController(
        workspace.workspaces,
        keepSymbolLayoutTarget(widget, widgetLayoutTarget(widget), readLook, applyLook),
      );
    } catch {
      layouts = null;
    }
    const openLayouts = (anchor: HTMLElement): void => {
      if (!layouts || !widget) return;
      void openLayoutsMenu(widget.context, layouts, anchor).catch(() => undefined);
    };
    openLayoutsRef.current = layouts ? openLayouts : null;

    const publishChrome = (): void => {
      if (!widget) return;
      paintCrosshairButton(widget.root, crosshairOnRef.current, toggleCrosshair);
      paintSessionControl(
        widget.root,
        extendedOnRef.current,
        chooseSession,
        sessionEnabledRef.current,
      );
      paintLayoutsButton(widget.root, openLayoutsRef.current);
      onChromeRef.current?.(linkSlot(widget.root));
    };

    stops.push(widget.on('symbol', ({ symbol: next }) => {
      if (restoringRef.current) return;
      if (next === symbolRef.current) return;
      onSymbolRef.current?.(next);
      report();
    }));
    stops.push(widget.on('interval', ({ interval: code }) => {
      if (restoringRef.current) return;
      if (code === intervalRef.current) return;
      if (!fromOpenAlgoInterval(code)) {
        try {
          widget?.setInterval(intervalRef.current);
        } catch {
          // The previous code is unknown to this build. Leave the widget where it is.
        }
        return;
      }
      onIntervalRef.current?.(code);
      report();
    }));
    stops.push(widget.on('layout', ({ chartType: next }) => {
      if (restoringRef.current) return;
      if (next && next !== chartTypeRef.current) onChartTypeRef.current?.(next);
      report();
    }));
    try {
      stops.push(widget.chart.on('layout:change', () => reportSoon()));
      stops.push(widget.chart.on('pan', () => rememberView()));
      stops.push(widget.chart.on('zoom', () => rememberView()));
      stops.push(widget.chart.on('indicatorSettings', (payload: unknown) => {
        openIndicatorSettings(widget, instanceIdFrom(payload));
      }));
    } catch {
      // The bus is optional on a test double.
    }
    stops.push(widget.history.subscribe(() => reportSoon()));
    const releaseFields = armSymbolField(widget.root);
    const stopSessionWatch = watchSessionSettings(
      widget.root,
      () => shadeRef.current,
      (next) => onShadeRef.current?.(next),
    );
    publishChrome();
    const chromeObserver = new MutationObserver(() => {
      armSymbolField(widget?.root ?? el);
      publishChrome();
    });
    chromeObserver.observe(widget.root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

    let cancelled = false;
    void widget.ready.then(() => {
      if (cancelled || !widget || widget.isDestroyed) return;
      restoringRef.current = true;
      try {
        const saved = savedRef.current;
        const apply = (): void => {
          if (saved != null) {
            try {
              widget?.restoreState(saved);
            } catch {
              // A bad saved layout is skipped. The chart still draws the bars.
            }
          }
          if (!widget) return;
          if (widget.symbol() !== symbolRef.current) widget.setSymbol(symbolRef.current);
          if (widget.interval() !== intervalRef.current) widget.setInterval(intervalRef.current);
          if (widget.chartType() !== chartTypeRef.current) widget.setChartType(chartTypeRef.current);
          if (seedRef.current) {
            try {
              widget.chart.addIndicator(ZERODAY_ID);
            } catch {
              // The study is already on the chart, or this build has no study slot.
            }
          }
        };
        widget.history.ignore(apply);
      } catch {
        // A bad saved layout is skipped. The chart still draws the bars.
      } finally {
        restoringRef.current = false;
      }
      if (!widget || widget.isDestroyed) return;
      bindChartWatermark(widget.chart);
      applyCrosshair(widget, candleTheme(), crosshairOnRef.current, blankedCrosshair);
      armSymbolField(widget.root);
      publishChrome();
      acceptEditsRef.current = true;
      report();
    });

    return () => {
      cancelled = true;
      if (reportTimer != null) {
        clearTimeout(reportTimer);
        reportTimer = null;
        report();
      }
      chromeObserver.disconnect();
      stopSessionWatch();
      releaseFields();
      onChromeRef.current?.(null);
      openLayoutsRef.current = null;
      stops.forEach((stop) => stop());
      try {
        layouts?.destroy();
      } catch {
        // The controller was already stopped.
      }
      widget?.destroy();
      widgetRef.current = null;
      keyRef.current = null;
      prevRef.current = [];
    };
  }, []);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget || widget.symbol() === symbol) return;
    try {
      widget.setSymbol(symbol);
    } catch {
      // The widget was destroyed between render and this effect.
    }
  }, [symbol]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget || widget.interval() === intervalCode) return;
    try {
      widget.setInterval(intervalCode);
    } catch {
      // Unknown code. The parent keeps the previous interval.
    }
  }, [intervalCode]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget || widget.chartType() === chartType) return;
    try {
      widget.setChartType(chartType);
    } catch {
      // A type this build cannot draw. Leave the current one.
    }
  }, [chartType]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) return;
    applyCrosshair(widget, candleTheme(), crosshair, blankedCrosshair);
    paintCrosshairButton(widget.root, crosshair, () => {
      onCrosshairRef.current?.(!crosshair);
    });
    paintSessionControl(
      widget.root,
      extendedHours,
      (extended) => onExtendedRef.current?.(extended),
      sessionEnabled,
    );
    paintLayoutsButton(widget.root, openLayoutsRef.current);
  }, [crosshair, extendedHours, sessionEnabled]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) return;
    bindChartWatermark(widget.chart);
  }, [symbol, intervalCode]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget || !extendedHours || !sessionEnabled) return undefined;
    let shading: { destroy: () => void } | null = null;
    try {
      const color = shadeCss(shadeRef.current);
      shading = attachSessionShading(widget.chart, {
        visible: true,
        preColor: color,
        postColor: color,
        extendedColor: null,
      });
    } catch {
      return undefined;
    }
    return () => {
      try {
        shading?.destroy();
      } catch {
        // The chart was already destroyed.
      }
    };
  }, [extendedHours, sessionEnabled, sessionShade]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) return undefined;
    let cancelled = false;
    const apply = (): void => {
      if (cancelled || widget.isDestroyed) return;
      const mapped = toOpenAlgoBars(bars);
      const sameSeries = keyRef.current === seriesKey && prevRef.current.length > 0;
      const keep = keptRange(widget.chart, sameSeries);
      const run = (): void => {
        const kind = pushSeries(widget.series, mapped, seriesKey, keyRef, prevRef);
        if (kind === 'tail') return;
        if (keep) {
          widget.chart.setVisibleLogicalRange(keep);
          return;
        }
        const saved = lookupZoom(viewsRef.current, symbolRef.current, intervalRef.current);
        if (!saved || mapped.length === 0) return;
        widget.chart.setVisibleLogicalRange(placeZoom(saved, mapped.length));
      };
      widget.history.ignore(run);
    };
    void widget.ready.then(apply);
    return () => {
      cancelled = true;
    };
  }, [bars, seriesKey]);

  if (failed) {
    return (
      <div role="alert" className="flex h-full items-center justify-center px-4 text-center text-sm text-gray-400">
        Chart failed to draw. {failed}
      </div>
    );
  }

  return <div ref={hostRef} className="h-full w-full min-h-0" />;
}
